/**
 * The National agent (Phase 4C), replacing the text table on "How National can help": service tiles, an
 * "Ask National" chat, and the firm's own requests. When the agent has no clear answer it proposes a
 * request; nothing reaches National IT until the firm's IT lead clicks Send. National IT answers requests
 * on the National view and can publish a generic version, which the agent then uses for everyone.
 */
import { NATIONAL_REQUEST_STATUS } from '@grc/shared/vocabulary';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, useApi, useApiClient, type Loadable } from '../api';
import { Card, formatDate, Load, Status } from '../components';
import type { Me, NationalRequest } from '../types';
import { ChatLog, RichText } from './AssistantPanel';
import { PlanProposal, type ProposedPlan } from './PlanProposal';

const CONTEXT = 'Guidance: how National can help';

const STARTERS = [
  'How do we join the National Intune framework?',
  'Which policy templates can we start from?',
  'What should we do first in a security incident?',
];

interface Turn {
  role: 'user' | 'assistant';
  content: string;
}

interface Proposal {
  question: string;
  summary: string;
}

/** The chat, for the firm the page takes from the person's group. */
export function AskNational({ firm: current, onSent, onAsked, prefill }: {
  firm: Me['firms'][number];
  onSent: () => void;
  /** Called once a question is answered, so the page can drop a handed-over question from its address (CP-01). */
  onAsked?: () => void;
  prefill?: { text: string; nonce: number };
}) {
  const call = useApiClient();
  const firm = current.firmCode;
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState('');
  const [proposal, setProposal] = useState<(Proposal & { canSend: boolean }) | null>(null);
  const [plan, setPlan] = useState<{ plan: ProposedPlan; canAdd: boolean } | null>(null);
  const [busy, setBusy] = useState<null | 'ask' | 'send' | 'plan'>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const questionRef = useRef<HTMLTextAreaElement>(null);
  // A question handed over from elsewhere on the page ("Ask National about ..."): fill it in for the person to send.
  const [seenPrefill, setSeenPrefill] = useState<number | undefined>();
  if (prefill && prefill.nonce !== seenPrefill) {
    setSeenPrefill(prefill.nonce);
    setMessage(prefill.text);
  }
  useEffect(() => {
    if (!prefill) return;
    questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    questionRef.current?.focus();
  }, [prefill]);

  const ask = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy('ask');
    setError(null);
    setSent(null);
    setProposal(null);
    setPlan(null);
    try {
      const r = await call<{ reply: string; proposeRequest: Proposal | null; proposePlan: ProposedPlan | null; canSend: boolean }>('/national-agent/turn', {
        method: 'POST',
        body: { firmCode: firm, message: q, context: CONTEXT, history: turns },
      });
      setTurns((t) => [...t, { role: 'user', content: q }, { role: 'assistant', content: r.reply }]);
      setMessage('');
      onAsked?.();
      if (r.proposeRequest) setProposal({ ...r.proposeRequest, canSend: r.canSend });
      if (r.proposePlan) setPlan({ plan: r.proposePlan, canAdd: r.canSend });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(message);
  };

  // "Track as an action plan": the agent proposes a plan for the main unplanned gap in the conversation.
  const track = async () => {
    setBusy('plan');
    setError(null);
    try {
      const r = await call<{ proposePlan: ProposedPlan }>('/national-agent/plan', { method: 'POST', body: { firmCode: firm, context: CONTEXT, history: turns } });
      setPlan({ plan: r.proposePlan, canAdd: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!proposal) return;
    setBusy('send');
    setError(null);
    try {
      await call(`/firms/${firm}/national-requests`, { method: 'POST', body: { question: proposal.question, summary: proposal.summary || null, context: CONTEXT } });
      setProposal(null);
      setSent('Sent to National IT. The answer appears under Your requests.');
      onSent();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const firmName = current.displayName;

  return (
    <section className="assistant-side ask-national" aria-labelledby="ask-h" id="ask">
      <h2 id="ask-h">Ask National</h2>
      <p className="assistant-intro">Answers come from National services, policies, templates and answers National IT has published. It never commits National IT to anything.</p>
      <ChatLog count={turns.length + (busy === 'ask' ? 1 : 0)}>
        {turns.length === 0 && (
          <li className="starters">
            <span className="assistant-note">Try one of these, or ask your own.</span>
            <span className="starter-list">
              {STARTERS.map((s) => (
                <button key={s} type="button" className="starter" onClick={() => void ask(s)} disabled={busy !== null}>
                  {s}
                </button>
              ))}
            </span>
          </li>
        )}
        {turns.map((t, i) => (
          <li key={i} className={`bubble ${t.role}`}>
            <span className="who">{t.role === 'user' ? 'You' : 'National agent'}</span>
            {t.role === 'assistant' ? <RichText text={t.content} /> : <p className="pre">{t.content}</p>}
          </li>
        ))}
        {busy === 'ask' && (
          <li className="bubble assistant" role="status">
            <span className="who">National agent</span>
            <p>Checking National content…</p>
          </li>
        )}
      </ChatLog>

      {current.canWrite && turns.some((t) => t.role === 'assistant') && !plan && (
        <button type="button" className="pill secondary-dark track-plan" disabled={busy !== null} onClick={() => void track()}>
          {busy === 'plan' ? 'Drafting a plan…' : 'Track a gap as an action plan'}
        </button>
      )}
      {plan && <PlanProposal firmCode={firm} plan={plan.plan} canAdd={plan.canAdd} onDone={() => setPlan(null)} />}
      {proposal && (
        <div className="proposal" role="region" aria-label="Send this question to National IT?">
          <h3>Send this question to National IT?</h3>
          {proposal.canSend ? (
            <label>
              Question
              <textarea rows={3} maxLength={4000} value={proposal.question} onChange={(e) => setProposal({ ...proposal, question: e.target.value })} />
            </label>
          ) : (
            <p className="pre">{proposal.question}</p>
          )}
          {proposal.summary && (
            <p className="small">
              <strong>What the agent found:</strong> {proposal.summary}
            </p>
          )}
          <p className="small muted-dark">It goes to National IT for {firmName}, with your name.</p>
          {proposal.canSend ? (
            <div className="actions">
              <button className="pill" onClick={send} disabled={busy !== null || proposal.question.trim().length < 10}>
                {busy === 'send' ? 'Sending…' : 'Send to National IT'}
              </button>
              <button type="button" className="pill secondary-dark" onClick={() => setProposal(null)} disabled={busy !== null}>
                Not now
              </button>
            </div>
          ) : (
            <p className="small">Only your firm's IT lead can send requests to National IT.</p>
          )}
        </div>
      )}
      {sent && (
        <p role="status" className="sent">
          {sent}
        </p>
      )}

      <form onSubmit={submit} className="form">
        <label>
          Your question
          <textarea ref={questionRef} rows={3} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} disabled={busy !== null} />
        </label>
        <div className="actions">
          <button className="pill" disabled={busy !== null || !message.trim()}>
            {busy === 'ask' ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </form>
      {error && (
        <p role="alert" className="assistant-error">
          {error}
        </p>
      )}
    </section>
  );
}

/** A firm's requests to National IT and the answers. */
export function FirmRequests({ firmCode, firmName, nonce }: { firmCode: string; firmName: string; nonce: number }) {
  // A new nonce (after a request is sent) fetches the list again.
  const state = useApi<{ requests: NationalRequest[] }>(`/firms/${firmCode}/national-requests${nonce ? `?after=${nonce}` : ''}`);
  return (
    <Card title="Your requests" id="your-requests">
      <p className="muted small">Questions {firmName} sent to National IT, newest first.</p>
      <Load state={state} what="your requests">
        {({ requests }) =>
          requests.length ? (
            <ul className="plain requests">
              {requests.map((r) => (
                <li key={r.id} className="request">
                  <p className="request-head">
                    <Status map={NATIONAL_REQUEST_STATUS} value={r.status} />
                    <span className="muted small">
                      {formatDate(r.askedAt)}, {r.askedBy}
                    </span>
                  </p>
                  <p className="request-question">{r.question}</p>
                  {r.answer && (
                    <div className="request-answer">
                      <p className="small muted">
                        {r.status === 'closed' ? 'Closed' : 'Answered'} by {r.answeredBy} on {formatDate(r.answeredAt)}
                      </p>
                      <RichText text={r.answer} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No requests yet. Ask National above; if it has no clear answer, you can send the question here.</p>
          )
        }
      </Load>
    </Card>
  );
}

export interface NationalQueue {
  canAnswer: boolean;
  requests: NationalRequest[];
}

/** National IT's open requests from firms (National IT only, DEC-33). The National view reads it once for its lead line and the card. */
export const useNationalQueue = () => useApi<NationalQueue>('/national/requests');

/** National IT's queue of firm requests, with an answer form and the option to publish a generic answer. */
export function NationalRequestsCard({ state }: { state: Loadable<NationalQueue> }) {
  const [done, setDone] = useState<string | null>(null);
  const [answering, setAnswering] = useState<number | null>(null);
  return (
    <Card title="Requests from firms">
      <p className="muted small">Requests firms sent through the National agent when it could not answer clearly. Open requests, oldest first.</p>
      {done && (
        <p role="status" className="ok">
          {done}
        </p>
      )}
      <Load state={state} what="requests from firms">
        {({ canAnswer, requests }) =>
          requests.length ? (
            <ul className="plain requests">
              {requests.map((r) => (
                <li key={r.id} className="request">
                  <p className="request-head">
                    <strong>{r.firmName}</strong>
                    <span className="muted small">
                      {formatDate(r.askedAt)}, {r.askedBy}
                    </span>
                  </p>
                  <p className="request-question">{r.question}</p>
                  {r.summary && <p className="small muted">What the agent found: {r.summary}</p>}
                  {canAnswer &&
                    (answering === r.id ? (
                      <AnswerForm
                        request={r}
                        onCancel={() => setAnswering(null)}
                        onDone={(text) => {
                          setDone(text);
                          setAnswering(null);
                          state.reload();
                        }}
                      />
                    ) : (
                      <button className="pill secondary-dark" aria-label={`Answer ${r.firmName}'s request`} onClick={() => (setAnswering(r.id), setDone(null))}>
                        Answer
                      </button>
                    ))}
                </li>
              ))}
            </ul>
          ) : (
            <p>No open requests.</p>
          )
        }
      </Load>
    </Card>
  );
}

function AnswerForm({ request, onDone, onCancel }: { request: NationalRequest; onDone: (text: string) => void; onCancel: () => void }) {
  const call = useApiClient();
  const [answer, setAnswer] = useState('');
  const [publish, setPublish] = useState(false);
  const [pubQuestion, setPubQuestion] = useState(request.question);
  const [pubAnswer, setPubAnswer] = useState('');
  const [topics, setTopics] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = `Answer the request from ${request.firmName}`;

  const save = async (status: 'answered' | 'closed') => {
    setBusy(true);
    setError(null);
    try {
      const r = await call<{ publishedAnswerId: number | null }>(`/national/requests/${request.id}/answer`, {
        method: 'POST',
        body: { answer, status, publish: publish && status === 'answered' ? { question: pubQuestion, answer: pubAnswer || answer, topics: topics || null } : null },
      });
      onDone(r.publishedAnswerId ? `Answer sent to ${request.firmName} and published as National knowledge.` : status === 'closed' ? `Request from ${request.firmName} closed.` : `Answer sent to ${request.firmName}.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form answer-form" aria-label={label} onSubmit={(e) => (e.preventDefault(), void save('answered'))}>
      <label>
        Answer for {request.firmName}
        <textarea rows={4} minLength={10} maxLength={8000} required value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </label>
      <label className="check">
        <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} />
        Publish as National knowledge (rewrite without firm details)
      </label>
      {publish && (
        <fieldset className="publish">
          <legend className="small">What every firm and the National agent will see</legend>
          <p className="notice small">Remove the firm's name, people, places and system details. The portal refuses text that names a firm.</p>
          <label>
            Question, in general terms
            <textarea rows={2} minLength={10} maxLength={1000} required value={pubQuestion} onChange={(e) => setPubQuestion(e.target.value)} />
          </label>
          <label>
            Answer, in general terms <span className="muted small">(leave empty to use the answer above)</span>
            <textarea rows={4} maxLength={8000} value={pubAnswer} onChange={(e) => setPubAnswer(e.target.value)} />
          </label>
          <label>
            Topics <span className="muted small">(optional, comma separated)</span>
            <input type="text" maxLength={400} value={topics} onChange={(e) => setTopics(e.target.value)} />
          </label>
        </fieldset>
      )}
      <div className="actions">
        <button className="pill" disabled={busy || answer.trim().length < 10}>
          {busy ? 'Sending…' : 'Send answer'}
        </button>
        <button type="button" className="pill secondary-dark" disabled={busy || answer.trim().length < 10} onClick={() => void save('closed')}>
          Close with this note
        </button>
        <button type="button" className="link" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </form>
  );
}
